import { ReactNode, Dispatch, SetStateAction, useContext } from "react";
import { Box, kafSystemWidth, systemWidth, useTheme } from "@kaltura/mediaspace-shared-styled";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { styled } from "@mui/material";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";

/**
 * Width of the right panel, in percents
 */
const rightPanelWidth = 33;

export const rightPanelLayoutMobileBreakpoint = "md";

export const useIsMobileRightPanelLayout = () => {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.down(rightPanelLayoutMobileBreakpoint));
};

export interface RightPanelLayoutProps {
    hero: ReactNode;
    details: ReactNode;
    // use component to send callback to right panel content in case there is no data and details should expand
    rightPanel?: KmsTypeComponent;
    collapseHero?: boolean;
    isEmptyRightPanel: boolean;
    setIsEmptyRightPanel: Dispatch<SetStateAction<boolean>>;
}

/**
 * Layout with a hero section (e.g. player), details section below it, and a panel from the right side.
 * The hero section could be collapsed, meaning that the right panel will push it to the left.
 *
 * Expanded hero (a.k.a. theater mode):
 * .---------------------.
 * |                     |
 * |        HERO         |
 * |                     |
 * |-----------.---------|
 * |  DETAILS  |  RIGHT  |
 * |           |  PANEL  |
 * '-----------'---------'
 *
 * Collapsed hero:
 * .-----------.---------.
 * |   HERO    |  RIGHT  |
 * |-----------|  PANEL  |
 * |  DETAILS  |         |
 * '-----------'---------'
 *
 * The layout could be switched between "expanded" and "collapsed" on the fly. The state of the inner components will be preserved.
 */
export const RightPanelLayout = ({
    hero,
    details,
    rightPanel,
    collapseHero = false,
    isEmptyRightPanel,
    setIsEmptyRightPanel,
}: RightPanelLayoutProps) => {
    const isMobile = useIsMobileRightPanelLayout();
    const RightPanelComponent = rightPanel?.component;

    const baseClass = "kms-ds-media-page-right-panel-layout";

    if (isMobile) {
        collapseHero = false;
    }
    // system width should be disabled in case of kaf
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    if (!rightPanel) {
        return (
            <div className={baseClass}>
                <div className={baseClass + "-hero"}>{hero}</div>
                <StyledBottomContainer collapseHero={false} className={baseClass + "-bottom"} isKaf={isKaf}>
                    <StyledDetailsContainer fullSize={true} className={baseClass + "-details"}>
                        {details}
                    </StyledDetailsContainer>
                </StyledBottomContainer>
            </div>
        );
    }

    const handleEmptyRightPanel = () => {
        setIsEmptyRightPanel(true);
    };

    // Note: it's important to preserve the same DOM/component tree when switching between "expanded" and "collapsed" layout to keep components' state
    return (
        <StyledOuterContainer collapseHero={collapseHero} className={baseClass} isKaf={isKaf}>
            <StyledHeroContainer collapseHero={collapseHero} className={baseClass + "-hero"}>
                {hero}
            </StyledHeroContainer>
            <StyledBottomContainer
                collapseHero={collapseHero}
                isMobile={isMobile}
                className={baseClass + "-bottom"}
                isKaf={isKaf}
            >
                {!isEmptyRightPanel && (
                    <StyledRightPanelContainer
                        collapseHero={collapseHero}
                        isMobile={isMobile}
                        className={baseClass + "-panel"}
                    >
                        {RightPanelComponent && (
                            <RightPanelComponent onEmptyData={handleEmptyRightPanel} {...rightPanel.props} />
                        )}
                    </StyledRightPanelContainer>
                )}
                <StyledDetailsContainer fullSize={isMobile} className={baseClass + "-details"}>
                    {details}
                </StyledDetailsContainer>
            </StyledBottomContainer>
        </StyledOuterContainer>
    );
};

const StyledOuterContainer = styled(
    Box,
    {
        shouldForwardProp(propName) {
            return propName !== "collapseHero" && propName !== "isKaf";
        },
    }
)<{ collapseHero: boolean; isKaf?: boolean }>(({ theme, collapseHero, isKaf }) => [
    collapseHero && {
        position: "relative",
        paddingTop: theme.spacing(4),
    },
    collapseHero
        && !isKaf && {
            ...systemWidth({ theme }),
        },
    {
        "&:after": {
            content: "''",
            display: "block",
            clear: "both",
        },
    },
]);

const StyledBottomContainer = styled(
    Box,
    {
        shouldForwardProp(propName) {
            return propName !== "collapseHero" && propName !== "isMobile" && propName !== "isKaf";
        },
    }
)<{ collapseHero?: boolean; isMobile?: boolean; isKaf?: boolean }>(({ theme, collapseHero, isMobile, isKaf }) => [
    !collapseHero && (isKaf ? kafSystemWidth({ theme }) : systemWidth({ theme })),
    !collapseHero && {
        position: "relative",
        display: "flex",
        flexDirection: "row-reverse",
    },
    collapseHero && {
        display: "inline",
    },
    isMobile && {
        display: "flex",
        flexDirection: "column-reverse",
    },
]);

const StyledHeroContainer = styled(
    Box,
    {
        shouldForwardProp(propName) {
            return propName !== "collapseHero";
        },
    }
)(({ collapseHero }: { collapseHero: boolean }) => [
    collapseHero && {
        display: "inline-block",
        width: `${100 - rightPanelWidth}%`,
    },
]);

const StyledDetailsContainer = styled(
    Box,
    {
        shouldForwardProp(propName) {
            return propName !== "fullSize";
        },
    }
)<{ fullSize?: boolean }>(({ theme, fullSize }) => [
    {
        paddingTop: theme.spacing(2),
        [theme.breakpoints.up("sm") ?? ""]: {
            paddingTop: theme.spacing(3),
        },
        flexGrow: 1,
    },
    !fullSize && {
        width: `${100 - rightPanelWidth}%`,
    },
]);

const StyledRightPanelContainer = styled(
    Box,
    {
        shouldForwardProp(propName) {
            return propName !== "collapseHero" && propName !== "isMobile";
        },
    }
)<{ collapseHero?: boolean; isMobile?: boolean }>(({ theme, collapseHero, isMobile }) => [
    {
        float: "right",
    },
    !collapseHero && {
        paddingTop: theme.spacing(isMobile ? 4 : 3),
    },
    !isMobile && {
        width: `${rightPanelWidth}%`,
        paddingLeft: theme.spacing(2),
        [theme.breakpoints.up("lg")]: {
            paddingLeft: theme.spacing(4),
        },
    },
]);
